import React, { useState } from "react";
import { all_routes } from "../../router/all_routes";
import { Link } from "react-router-dom";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import useApi from "../../../core/customhook/useApi";
import Loader from "../../../core/common/selfLoder";
import Table from "../../../core/common/dataTable/index";
import axios from "axios";
import { apibaseurl, jwttoken } from "../../../environment";
import Swal from "sweetalert2";
import { useSpinner } from "../../../core/common/spinnerContex";
import dayjs from "dayjs";

interface Destination {
  ID: number;
  destination_from: string;
  destination_to: string;
}

interface TravelInfo {
  ID: number;
  date_from: string;
  date_to: string;
  reason_of_travel: string;
  status: string;
  destination: Destination[];
}

interface TravelApiResponse {
  success: boolean;
  message: string;
  statusCode: number;
  data: TravelInfo[];
}

const Expense = () => {
  const {
    data: travelResponse,
    loading,
    error,
  } = useApi<TravelApiResponse>("/api/get-travel-info", {
    method: "GET",
    autoCall: true,
  });

  const [activeTab, setActiveTab] = useState("EXPC20251001");
  const [deleting, setDeleting] = useState(false);
  const [modalData, setModalData] = useState<any>({});
  const [editData, setEditData] = useState({
    ID: "",
    purpose: "",
    amount: "",
    note: "",
  });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const tabs = [
    { key: "EXPC20251001", label: "Accommodation" },
    { key: "EXPC20251002", label: "Travel" },
    { key: "", label: "Other Reimbursement" },
    { key: "advance", label: "Advance Expense" },
  ];

  const formatDMYToLongDate = (dateString: string | null): string => {
    if (!dateString) return "";

    const [day, month, year] = dateString.split("-").map(Number);
    if (!day || !month || !year) return "";

    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };


  const commonBaseColumns = [
    {
      title: "Sr. No.",
      dataIndex: "Sno",
    },
    {
      title: "Title",
      dataIndex: "expc_title",
      sorter: (a: any, b: any) =>
        new Date(a.expenseDate).getTime() - new Date(b.expenseDate).getTime(),
      width:"250px"
    },
    {
      title: "Cost Center",
      dataIndex: "costcenter_code",
      render: (_: any, record: any) =>
        `${record.costcenter_code} - ${record.costcentre_name}`,
      sorter: (a: any, b: any) =>
        a.costcenter_code.localeCompare(b.costcenter_code),
    },
    {
      title: "Date",
      dataIndex: "expc_date",
      render: (text: string) => formatDMYToLongDate(text),
      sorter: (a: any, b: any) => {
        const [dayA, monthA, yearA] = a.expc_date.split("-").map(Number);
        const [dayB, monthB, yearB] = b.expc_date.split("-").map(Number);
        return new Date(yearA, monthA - 1, dayA).getTime() - new Date(yearB, monthB - 1, dayB).getTime();
      },
    },
    {
      title: "Currency",
      dataIndex: "currency_name",
      render: (text: string) => text?.split("-")[0],
      sorter: (a: any, b: any) => a.currency.localeCompare(b.currency),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      sorter: (a: any, b: any) => a.amount - b.amount,
      render: (amount: number) => new Intl.NumberFormat("en-IN").format(amount), // This adds commas
    },
  ];

  const commonEndColumns = [
    {
      title: "Bill/Document",
      dataIndex: "receipt",
      render: (file: string) =>
        file ? (
          <a
            href={file}
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: "1.5rem", color: "red" }}
          >
            <i className="bi bi-file-earmark-text me-2"></i>
          </a>
        ) : (
          "No Bill/Document"
        ),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (text: string) => (
        <span
          className={`badge ${text === "Approved"
            ? "bg-success"
            : text === "Pending"
              ? "bg-warning"
              : "bg-danger"
            }`}
        >
          {text}
        </span>
      ),
      sorter: (a: any, b: any) => a.status.localeCompare(b.status),
    },
    {
      title: "Action",
      dataIndex: "actions",
      render: (_: any, record: any) => {
        const isApproved =
          record.status === "Approved" || record.status === "Rejected";

        return (
          <div className="action-icon d-inline-flex">
            <Link
              to="#"
              data-bs-toggle="modal"
              data-bs-target={`${record.status === "Approved"
                ? "#approve-modal"
                : record.status === "Rejected"
                  ? "#rejected-modal"
                  : "#apply-modal"
                }`}
              onClick={() => {
                setModalData(record);
              }}
              title="View"
              style={{ cursor: "pointer" }}
            >
              <i className="ti ti-eye" />
            </Link>

            <Link
              to={!isApproved ? "/edit-expense-request" : "#"}
              state={!isApproved ? { expenseData: record } : null}
              onClick={(e) => {
                if (isApproved) e.preventDefault();
              }}
              style={{
                cursor: isApproved ? "not-allowed" : "pointer",
              }}
            >
              <i className="ti ti-edit" />
            </Link>

            <Link
              to="#"
              onClick={(e) => {
                if (!isApproved) {
                  deleteExpenses(record?.expense_id);
                } else {
                  e.preventDefault();
                }
              }}
              style={{
                cursor: isApproved ? "not-allowed" : "pointer",
              }}
            >
              <i className="ti ti-trash" />
            </Link>
          </div>
        );
      },
    },
  ];

  const accommodationColumns = [
    {
      title: "Country",
      dataIndex: "acc_country",
    },
    {
      title: "City",
      dataIndex: "acc_city",
    },
    {
      title: "Check In",
      dataIndex: "acc_check_in",
      render: (text: string) => formatDMYToLongDate(text),
      sorter: (a: any, b: any) => {
        const [dayA, monthA, yearA] = a.acc_check_in.split("-").map(Number);
        const [dayB, monthB, yearB] = b.acc_check_in.split("-").map(Number);
        return new Date(yearA, monthA - 1, dayA).getTime() - new Date(yearB, monthB - 1, dayB).getTime();
      },
    },
    {
      title: "Check Out",
      dataIndex: "acc_check_out",
      render: (text: string) => formatDMYToLongDate(text),
      sorter: (a: any, b: any) => {
        const [dayA, monthA, yearA] = a.acc_check_out.split("-").map(Number);
        const [dayB, monthB, yearB] = b.acc_check_out.split("-").map(Number);
        return new Date(yearA, monthA - 1, dayA).getTime() - new Date(yearB, monthB - 1, dayB).getTime();
      },
    },
  ];

  const travelColumns = [
    {
      title: "Type",
      dataIndex: "travel_trip_type",
    },
    {
      title: "Mode",
      dataIndex: "travel_mode",
    },
    {
      title: "Class",
      dataIndex: "travel_class",
    },
    {
      title: "From",
      dataIndex: "travel_from_port",
    },
    {
      title: "To",
      dataIndex: "travel_to_port",
    },
    {
      title: "Onward",
      dataIndex: "travel_date_onward",
      render: (text: string) => formatDMYToLongDate(text),
      sorter: (a: any, b: any) => {
        const [dayA, monthA, yearA] = a.travel_date_onward.split("-").map(Number);
        const [dayB, monthB, yearB] = b.travel_date_onward.split("-").map(Number);
        return new Date(yearA, monthA - 1, dayA).getTime() - new Date(yearB, monthB - 1, dayB).getTime();
      },
    },
    {
      title: "Return",
      dataIndex: "travel_date_return",
      render: (text: string) => formatDMYToLongDate(text),
      sorter: (a: any, b: any) => {
        const [dayA, monthA, yearA] = a.travel_date_return.split("-").map(Number);
        const [dayB, monthB, yearB] = b.travel_date_return.split("-").map(Number);
        return new Date(yearA, monthA - 1, dayA).getTime() - new Date(yearB, monthB - 1, dayB).getTime();
      },
    },
  ];

  const otherColumns = [
    {
      title: "Category",
      dataIndex: "category_name",
      render: (text: string) => (
        <p className="fw-medium">{text?.split(" ")[1]}</p>
      ),
      sorter: (a: any, b: any) =>
        a.expenseCategory.localeCompare(b.expenseCategory),
    },
    {
      title: "Bill ",
      dataIndex: "bill_no",
    },
    {
      title: "Merchant",
      dataIndex: "merchant_name",
    },
    {
      title: "Receipt",
      dataIndex: "receipt",
      render: (file: string) =>
        file ? (
          <a
            href={file}
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: "1.5rem", color: "red" }}
          >
            <i className="bi bi-file-earmark-text me-2"></i>
          </a>
        ) : (
          "No Receipt"
        ),
    },
  ];

  const advanseColumns = [
    {
      title: "SNo.",
      dataIndex: "Sno",
      sorter: (a: any, b: any) => a.Sno - b.Sno,
    },
    {
      title: "Purpose",
      dataIndex: "purpose",
      render: (text: string) => <p className="fw-medium">{text}</p>,
      sorter: (a: any, b: any) => a.purpose.localeCompare(b.purpose),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      sorter: (a: any, b: any) => a.amount - b.amount,
      render: (amount: number) => new Intl.NumberFormat("en-IN").format(amount),
    },
    {
      title: "Note",
      dataIndex: "note",
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (text: string) => (
        <span
          className={`badge ${text === "Approved"
            ? "bg-success"
            : text === "Pending"
              ? "bg-warning"
              : "bg-danger"
            }`}
        >
          {text}
        </span>
      ),
      sorter: (a: any, b: any) => a.status.localeCompare(b.status),
    },
    {
      title: "Action",
      dataIndex: "actions",
      render: (_: any, record: any) => {
        const isApproved =
          record.status === "Approved" || record.status === "Rejected";

        return (
          <div className="action-icon d-inline-flex">
            <Link
              to="#"
              title="View"
              data-bs-toggle="modal"
              data-bs-target={`${record.status === "Approved"
                ? "#approve-modal"
                : record.status === "Rejected"
                  ? "#rejected-modal"
                  : "#advance-pending-modal"
                }`}
              onClick={() => {
                setModalData(record);
              }}
              style={{
                cursor: "pointer",
              }}
            >
              <i className="ti ti-eye" />
            </Link>

            {/* <Link
              to="#"
              data-bs-toggle="modal"
              data-bs-target={`${!isApproved? "#editAdvanseExpense" : ""}`}
              onClick={() => {
                if (!isApproved) setEditData(record);
              }}
              style={{
                cursor: isApproved ? "not-allowed" : "pointer",
              }}
            >
              <i className="ti ti-edit" />
            </Link> */}
            <Link
              to="#"
              {...(!isApproved && {
                "data-bs-toggle": "modal",
                "data-bs-target": "#editAdvanseExpense",
              })}
              onClick={(e) => {
                if (!isApproved) {
                  setEditData(record);
                } else {
                  e.preventDefault(); // Prevent opening modal
                }
              }}
              style={{
                cursor: isApproved ? "not-allowed" : "pointer",
              }}
            >
              <i className="ti ti-edit" />
            </Link>

            <Link
              to="#"
              onClick={() => {
                if (!isApproved) deleteAdvanseExpenses(record?.ID);
              }}
              style={{
                cursor: isApproved ? "not-allowed" : "pointer",
              }}
            >
              <i className="ti ti-trash" />
            </Link>
          </div>
        );
      },
    },
  ];

  const getColumns = () => {
    switch (activeTab) {
      case "EXPC20251001":
        return [
          ...commonBaseColumns,
          ...accommodationColumns,
          ...commonEndColumns,
        ];
      case "EXPC20251002":
        return [...commonBaseColumns, ...travelColumns, ...commonEndColumns];
      case "":
        return [...commonBaseColumns, ...otherColumns, ...commonEndColumns];
      case "advance":
        return [...advanseColumns];
      default:
        return [
          ...commonBaseColumns,
          ...accommodationColumns,
          ...commonEndColumns,
        ];
    }
  };

  const {
    data: expenseData,
    loading: expenseDataLoading,
    error: expenseDataError,
  } = useApi("/api/expenses/view", {
    method: "POST",
    autoCall: activeTab !== "advance",
    payload: {
      expc_category_id: activeTab,
    },
    dependencies: [activeTab, refreshTrigger],
  });

  const filteredExpenseData = expenseData?.data?.map(
    (item: any, index: number) => {
      return {
        ...item,
        Sno: index + 1,
      };
    }
  );

  const deleteExpenses = async (expense_id: string) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This will permanently delete the expense.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        setDeleting(true);
        const response = await axios.delete(
          `${apibaseurl}/api/expenses/delete/${expense_id}`,
          {
            headers: {
              Authorization: `Bearer ${jwttoken}`,
            },
          }
        );

        if (response.status === 200) {
          setDeleting(false);
          Swal.fire({
            title: "Success!",
            text: response.data.message || "Expense deleted successfully.",
            icon: "success",
            confirmButtonText: "OK",
          });
          setRefreshTrigger((prev) => prev + 1);
        }
      } catch (error: any) {
        setDeleting(false);
        Swal.fire({
          title: "Error!",
          text:
            error?.response?.data?.message ||
            "Something went wrong while deleting.",
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    }
  };

  const [data, setData] = useState({
    expansePurpose: "",
    expanseAmount: "",
    expanseMessage: "",
  });

  const getInputdata = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setData({ ...data, [name]: value });
  };

  const {
    data: expanseData,
    loading: expanseLoading,
    error: expanseError,
    callApi: postAdvanseexpentdata,
  } = useApi("/api/expenses/advance/add", {
    method: "POST",
    autoCall: false,
  });
  const postData = async (e: React.ChangeEvent<HTMLFormElement>) => {
    e.preventDefault();
    const payload = {
      purpose: data?.expansePurpose,
      amount: Number(data?.expanseAmount),
      note: data?.expanseMessage,
    };
    try {
      const response = await postAdvanseexpentdata(payload);
      if (response.success) {
        Swal.fire({
          title: "Success!",
          text: response?.message || "Advanse Expense successfully.",
          icon: "success",
          confirmButtonText: "OK",
        });
        setData({
          expansePurpose: "",
          expanseAmount: "",
          expanseMessage: "",
        });
        document.getElementById("closeAdvanceExpense")?.click()
      }
    } catch (error: any) {
      Swal.fire({
        title: "Error!",
        text:
          error?.response?.data?.message ||
          "Something went wrong while deleting.",
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };
  const {
    data: advanseExpenseData,
    loading: advanseExpenseLoading,
    error: advanseExpenseError,
    callApi: getAdvanseData,
  } = useApi("/api/expenses/advance/view", {
    method: "GET",
    autoCall: false,
  });


  const getData = async () => {
    try {
      const response = await getAdvanseData();
      // console.log(response);
    } catch (error) { }
  };

  // console.log(advanseExpenseData)

  const filteredAdvanseData = advanseExpenseData?.data?.map(
    (item: any, index: number) => ({
      ...item,
      Sno: index + 1,
    })
  );

  const tableData =
    activeTab === "advance" ? filteredAdvanseData : filteredExpenseData;

  const {
    data: deleteAdvanseExpense,
    loading: deleteadvanseexploading,
    error: deleteadvanseexpanseError,
    callApi: deleteAdvanseexp,
  } = useApi("", {
    method: "DELETE",
    autoCall: false,
  });

  const deleteAdvanseExpenses = async (advanseExpanseID: any) => {
    const confirmResult = await Swal.fire({
      title: "Are you sure?",
      text: "This action will delete the advance expense permanently.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it!",
    });

    if (confirmResult.isConfirmed) {
      try {
        const response = await deleteAdvanseexp(
          undefined,
          "/api/expenses/advance/delete/" + advanseExpanseID
        );

        if (response?.success) {
          Swal.fire({
            title: "Deleted!",
            text: response?.message || "Advance Expense deleted successfully.",
            icon: "success",
            confirmButtonText: "OK",
          });
          getData(); // Refresh data
        }
      } catch (error: any) {
        Swal.fire({
          title: "Error!",
          text:
            error?.response?.data?.message ||
            "Something went wrong while deleting.",
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    }
  };

  const {
    data: editAdvanseData,
    loading: editAdvanseLoading,
    error: editAdvanseError,
    callApi: editAdvanseRecord,
  } = useApi("/api/expenses/advance/edit", {
    method: "POST",
    autoCall: false,
  });

  const editAdvanseExpenseData = async (
    e: React.ChangeEvent<HTMLFormElement>
  ) => {
    e.preventDefault();
    const payload = {
      request_id: editData?.ID,
      purpose: editData?.purpose,
      amount: Number(editData?.amount),
      note: editData?.note,
    };
    try {
      const response = await editAdvanseRecord(payload);
      if (response?.success) {
        Swal.fire({
          title: "Success!",
          text: response?.message || "Advanse Expense Updated successfully.",
          icon: "success",
          confirmButtonText: "OK",
        });
        setEditData({
          ID: "",
          purpose: "",
          amount: "",
          note: "",
        });
        getData();

        document.getElementById("closeEditAdvanceExpense")?.click()
      }
    } catch (error: any) {
      Swal.fire({
        title: "Error!",
        text:
          error?.response?.data?.message ||
          "Something went wrong while deleting.",
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };
  return (
    <>
      {/* <Loader loading={loading || expenseDataLoading} />; */}
      <div className="page-wrapper">
        <div className="content">
          {/* Breadcrumb */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">Expense</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={all_routes.employeeDashboard}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">Work & Operations</li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Expense
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap ">
              <div className="me-2  mb-2">
                <Link
                  to="/expense-request"
                  className="btn btn-primary custom_button d-flex align-items-center"
                >
                  {" "}
                  New Expense Request
                </Link>
              </div>
              <div className="mb-2">
                <button
                  type="button"
                  className="btn btn-primary custom_button"
                  data-bs-toggle="modal"
                  data-bs-target="#exampleModalToggle"
                >
                  Advance Request
                </button>
              </div>
              <div className="head-icons ms-2">
                <CollapseHeader />
              </div>
            </div>
          </div>
          {/* /Breadcrumb */}
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
              <h5>Expense Requests List</h5>
            </div>

            <div className="card-body">
              <div className="d-flex flex-nowrap gap-2 mb-3 overflow-auto text-nowrap py-2">
                {tabs.map((tab) => (
                  <button
                    key={tab.key}
                    className={`btn px-2 px-sm-4`}
                    style={{
                      backgroundColor:
                        activeTab === tab.key ? "#2e3294" : "white",
                      color: activeTab === tab.key ? "#ffffff" : "#2e3294",
                      borderRadius: "15px",
                      fontWeight: "500",
                      border: "1px solid #2e3294",

                    }}
                    onClick={() => {
                      setActiveTab(tab.key);
                      if (tab.key === "advance") {
                        getData();
                      }
                    }}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
              <div className="card">
                <Table
                  dataSource={tableData}
                  columns={getColumns()}
                  Selection={false}
                  showSearchInput={true}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="footer d-flex align-items-center justify-content-center   border-top bg-white p-3">
          <p className="mb-0">© {new Date().getFullYear()} WeConnect. All rights reserved.</p>
        </div>
      </div>
      {/* /Page Wrapper */}
      <div
        className="modal fade"
        id="exampleModalToggle"
        aria-hidden="true"
        aria-labelledby="exampleModalToggleLabel"
        tabIndex={-1}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title" id="exampleModalToggleLabel">
                Advance Expense Request
              </h4>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                id="closeAdvanceExpense"
              />
            </div>
            <form onSubmit={postData}>
              <div className="modal-body">
                <div className="mb-3">
                  <label htmlFor="purposeInput" className="form-label">
                    Purpose of Advance Request
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="purposeInput"
                    placeholder="Enter the purpose"
                    name="expansePurpose"
                    onChange={getInputdata}
                    value={data.expansePurpose}
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="purposeInput" className="form-label">
                    Amount
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    id="amount"
                    placeholder="Enter the Amount"
                    name="expanseAmount"
                    onChange={getInputdata}
                    value={data.expanseAmount}
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="noteTextarea" className="form-label">
                    Add a Note for Approver
                  </label>
                  <textarea
                    className="form-control"
                    id="noteTextarea"
                    rows={3}
                    placeholder="Add a note"
                    name="expanseMessage"
                    onChange={getInputdata}
                    value={data.expanseMessage}
                  ></textarea>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-primary custom_button" type="submit">
                  Submit
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <div
        className="modal fade"
        id="editAdvanseExpense"
        aria-hidden="true"
        aria-labelledby="exampleModalToggleLabel"
        tabIndex={-1}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title" id="exampleModalToggleLabel">
                Edit Advance Expense Request
              </h4>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                id="closeEditAdvanceExpense"
              />
            </div>
            <form onSubmit={editAdvanseExpenseData}>
              <div className="modal-body">
                <div className="mb-3">
                  <label htmlFor="purposeInput" className="form-label">
                    Purpose of Advance Request
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="purposeInput"
                    placeholder="Enter the purpose"
                    onChange={(e) =>
                      setEditData({ ...editData, purpose: e.target.value })
                    }
                    value={editData?.purpose}
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="purposeInput" className="form-label">
                    Amount
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    id="amount"
                    placeholder="Enter the Amount"
                    onChange={(e) =>
                      setEditData({ ...editData, amount: e.target.value })
                    }
                    value={editData?.amount}
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="noteTextarea" className="form-label">
                    Add a Note for Approver
                  </label>
                  <textarea
                    className="form-control"
                    id="noteTextarea"
                    rows={3}
                    placeholder="Add a note"
                    onChange={(e) =>
                      setEditData({ ...editData, note: e.target.value })
                    }
                    value={editData?.note}
                  ></textarea>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-primary" type="submit">
                  Submit
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <div
        id="apply-modal"
        className="modal fade"
        tabIndex={-1}
        role="dialog"
        aria-labelledby="apply-modalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-lg modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header  text-white">
              <h5 className="modal-title" id="apply-modalLabel">
                Pending Expense Details
              </h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
              />
            </div>
            <div className="modal-body">
              <div className="row">
                <div className="col-md-4 mb-3">
                  <strong>Expense ID:</strong>{" "}
                  <p className="text-muted">{modalData?.expense_id || "N/A"}</p>
                </div>
                <div className="col-md-4 mb-3">
                  <strong>Expense Category:</strong>{" "}
                  <p className="text-muted">
                    {modalData?.expense_category_name || "N/A"}
                  </p>
                </div>
                <div className="col-md-4 mb-3">
                  <strong>Cost Center Code:</strong>{" "}
                  <p className="text-muted">
                    {modalData?.costcenter_code || "N/A"}
                  </p>
                </div>
                <div className="col-md-4 mb-3">
                  <strong>Cost Center Name:</strong>{" "}
                  <p className="text-muted">
                    {modalData?.costcentre_name || "N/A"}
                  </p>
                </div>
                <div className="col-md-4 mb-3">
                  <strong>Title:</strong>{" "}
                  <p className="text-muted">{modalData?.expc_title || "N/A"}</p>
                </div>
                <div className="col-md-4 mb-3">
                  <strong>Expense Date:</strong>{" "}
                  <p className="text-muted">  {formatDMYToLongDate(modalData?.expc_date)}</p>
                </div>
                <div className="col-md-4 mb-3">
                  <strong>Currency:</strong>{" "}
                  <p className="text-muted">
                    {modalData?.currency_name || modalData?.currency || "N/A"}
                  </p>
                </div>
                <div className="col-md-4 mb-3">
                  <strong>Amount:</strong>{" "}
                  <p className="text-muted">{modalData?.amount || "N/A"}</p>
                </div>
                {modalData.expense_category_name?.split(" ")[1] ===
                  "Accommodation" ? (
                  <>
                    <div className="col-md-4 mb-3">
                      <strong>Check-in:</strong>{" "}
                      <p className="text-muted">
                        {formatDMYToLongDate(modalData?.acc_check_in || "N/A")}
                      </p>
                    </div>
                    <div className="col-md-4 mb-3">
                      <strong>Check-out:</strong>{" "}
                      <p className="text-muted">
                        {formatDMYToLongDate(modalData?.acc_check_out || "N/A")}
                      </p>
                    </div>
                    <div className="col-md-4 mb-3">
                      <strong>City:</strong>{" "}
                      <p className="text-muted">
                        {modalData?.acc_city || "N/A"}
                      </p>
                    </div>
                    <div className="col-md-4 mb-3">
                      <strong>Country:</strong>{" "}
                      <p className="text-muted">
                        {modalData?.acc_country || "N/A"}
                      </p>
                    </div>
                  </>
                ) : modalData.expense_category_name?.split(" ")[2] ===
                  "Travel" ? (
                  <>
                    <div className="col-md-4 mb-3">
                      <strong>Trip Type:</strong>{" "}
                      <p className="text-muted">
                        {modalData?.travel_trip_type || "N/A"}
                      </p>
                    </div>
                    <div className="col-md-4 mb-3">
                      <strong>Mode Of Travel:</strong>{" "}
                      <p className="text-muted">
                        {modalData?.travel_mode || "N/A"}
                      </p>
                    </div>
                    <div className="col-md-4 mb-3">
                      <strong>Class Of Travel:</strong>{" "}
                      <p className="text-muted">
                        {modalData?.travel_class || "N/A"}
                      </p>
                    </div>
                    <div className="col-md-4 mb-3">
                      <strong>From:</strong>{" "}
                      <p className="text-muted">
                        {modalData?.travel_from_port || "N/A"}
                      </p>
                    </div>
                    <div className="col-md-4 mb-3">
                      <strong>To:</strong>{" "}
                      <p className="text-muted">
                        {modalData?.travel_to_port || "N/A"}
                      </p>
                    </div>
                    <div className="col-md-4 mb-3">
                      <strong>Date of onward journey:</strong>{" "}
                      <p className="text-muted">
                        {formatDMYToLongDate(modalData?.travel_date_onward) || "N/A"}
                      </p>
                    </div>
                    <div className="col-md-4 mb-3">
                      <strong>Date of return journey:</strong>{" "}
                      <p className="text-muted">
                        {formatDMYToLongDate(modalData?.travel_date_return) || "N/A"}
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    {/* <div className="col-md-4 mb-3"><strong>Check-in:</strong> <p className="text-muted">{modalData?.acc_check_in || "N/A"}</p></div>
          <div className="col-md-4 mb-3"><strong>Check-out:</strong> <p className="text-muted">{modalData?.acc_check_out || "N/A"}</p></div>
          <div className="col-md-4 mb-3"><strong>City:</strong> <p className="text-muted">{modalData?.acc_city || "N/A"}</p></div>
          <div className="col-md-4 mb-3"><strong>Country:</strong> <p className="text-muted">{modalData?.acc_country || "N/A"}</p></div> */}
                  </>
                )}
                <div className="col-12 mb-3">
                  <strong>Comments:</strong>{" "}
                  <p className="text-muted">{modalData?.comments || "N/A"}</p>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-primary"
                data-bs-dismiss="modal"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
      <div
        id="advance-pending-modal"
        className="modal fade"
        tabIndex={-1}
        role="dialog"
        aria-labelledby="advancePendingLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-lg modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header text-dark">
              <h5 className="modal-title" id="advancePendingLabel">
                Pending Advance Request
              </h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
              />
            </div>
            <div className="modal-body">
              <div className="row">
                <div className="col-md-4 mb-3">
                  <strong>Purpose:</strong>{" "}
                  <p className="text-muted">{modalData?.purpose || "N/A"}</p>
                </div>
                <div className="col-md-4 mb-3">
                  <strong>Amount:</strong>{" "}
                  <p className="text-muted">{modalData?.amount || "N/A"}</p>
                </div>
                <div className="col-md-4 mb-3">
                  <strong>Request Date:</strong>{" "}
                  <p className="text-muted">
                    {modalData?.created_date
                      ? dayjs(modalData.created_date).format(
                        "DD-MM-YYYY hh:mm A"
                      )
                      : "N/A"}
                  </p>
                </div>
                <div className="col-md-12 mb-3">
                  <strong>Note:</strong>{" "}
                  <p className="text-muted">{modalData?.note || "N/A"}</p>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-primary"
                data-bs-dismiss="modal"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
      <div
        id="approve-modal"
        className="modal fade"
        tabIndex={-1}
        role="dialog"
        aria-labelledby="approve-modalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header text-white">
              <h5 className="modal-title" id="approve-modalLabel">
                Expense Approved
              </h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
              />
            </div>
            <div className="modal-body">
              <div className="mb-3">
                <strong>Approved By:</strong>
                <p className="text-muted">{modalData?.approver_id || "N/A"}</p>
              </div>
              <div className="mb-3">
                <strong>Approved On:</strong>
                <p className="text-muted">
                  {modalData?.approved_on
                    ? dayjs(modalData.approved_on).format("DD-MMMM-YYYY hh:mm A")
                    : "N/A"}
                </p>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-primary"
                data-bs-dismiss="modal"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
      <div
        id="rejected-modal"
        className="modal fade"
        tabIndex={-1}
        role="dialog"
        aria-labelledby="rejected-modalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header  text-white">
              <h5 className="modal-title" id="rejected-modalLabel">
                Expense Rejected
              </h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
              />
            </div>
            <div className="modal-body">
              <div className="mb-3">
                <strong>Rejected By:</strong>
                <p className="text-muted">{modalData?.approver_id || "N/A"}</p>
              </div>
              <div className="mb-3">
                <strong>Rejected On:</strong>
                <p className="text-muted">
                  {modalData?.rejected_on
                    ? dayjs(modalData.rejected_on).format("DD-MMMM-YYYY hh:mm A")
                    : "N/A"}
                </p>
              </div>
              <div className="mb-3">
                <strong>Rejection Reason:</strong>
                <p className="text-muted">
                  {modalData?.rejected_reason || "Not Provided"}
                </p>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-primary"
                data-bs-dismiss="modal"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Expense;
